import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Utils
import cn from '../../../utils/cn';
import formatDate from '../../../utils/formatDate';

// Domain
import useDateRange from '../../../domain/dateRange/useDateRange';
import { INITIAL_RANGE } from '../../../domain/dateRange/useDateRangeUseCase';
import { parseApiDateString } from '../../../domain/globalFilters/globalFiltersUseCase';
import { getDateRangeValueField } from '../../../domain/tableConditionalFilter/conditionalFiltersUseCase';

// Components
import { Popover } from '../../../store/popover/PopoverContext';
import { PopoverTrigger, PopoverContent } from '../../atoms/Popover/Popover';
import Picto from '../../atoms/Picto/Picto';
import FilterDateRangePicker from '../FilterDateRangePicker/FilterDateRangePicker';

// Styles
import styles from './ConditionalFiltersDateRange.module.css';

/** @type {ApiManagerDateRangeType} */
const DATE_RANGE_TYPE = 'custom';

/** @type {ApiManagerDateRangeType[]} */
const DATE_RANGE_TYPE_OPTIONS = ['custom'];

/**
 * Conditional filter date range field with calendar popover
 * @param {object} props
 * @param {string[]} [props.value]
 * @param {(values: string[]) => void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {string} [props.error]
 * @param {string} [props.id]
 * @returns {JSX.Element}
 */
function ConditionalFiltersDateRange({
  value = [],
  onChange,
  disabled = false,
  error = '',
  id: propId = 'conditional-filter-date-range',
}) {
  const { t } = useTranslation();

  const initialRanges = useMemo(() => {
    const { start, end } = getDateRangeValueField(value);
    const startDate = parseApiDateString(start) ?? new Date();
    const endDate = parseApiDateString(end) ?? startDate;

    return [{
      startDate,
      endDate,
      key: INITIAL_RANGE.key,
    }];
  }, [value]);

  const dateRange = useDateRange({
    initialRanges,
    type: DATE_RANGE_TYPE,
    typeOptions: DATE_RANGE_TYPE_OPTIONS,
    isLoading: false,
  });

  const {
    calendarOpen,
    setCalendarOpen,
    ranges,
  } = dateRange;

  const { start, end } = getDateRangeValueField(value);
  const startDate = parseApiDateString(start);
  const endDate = parseApiDateString(end);
  const hasCompleteRange = Boolean(startDate && endDate);

  const triggerLabel = hasCompleteRange
    ? t('date.dateRange', {
      start: formatDate(startDate, 'dd MMM. yy', 'fr'),
      end: formatDate(endDate, 'dd MMM. yy', 'fr'),
    })
    : t('table.filters.selectDateRange');

  /**
   * @param {ApiManagerDateRangeType} _type
   * @param {import('react-date-range').Range} newRange
   */
  const handleDateRangeMutation = (_type, newRange) => {
    if (!newRange?.startDate || !newRange?.endDate) return;

    onChange([
      formatDate(newRange.startDate, 'dd/MM/yyyy', 'fr', ''),
      formatDate(newRange.endDate, 'dd/MM/yyyy', 'fr', ''),
    ]);
  };

  return (
    <div
      className={cn([styles.container, error && styles['has-error']])}
      id={propId ? `${propId}-wrapper` : undefined}
    >
      <Popover
        open={calendarOpen}
        onOpenChange={setCalendarOpen}
        placement="bottom-start"
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn([styles.trigger, disabled && styles.disabled])}
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={calendarOpen}
            aria-describedby={error ? `${propId}-error` : undefined}
            onClick={() => setCalendarOpen(!calendarOpen)}
          >
            <span className={cn([
              styles['trigger-label'],
              !hasCompleteRange && styles.placeholder,
            ])}
            >
              {triggerLabel}
            </span>
            <Picto
              icon="calendar-main"
              className={styles.picto}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent className={styles['popover-content']}>
          <FilterDateRangePicker
            dateRange={dateRange}
            ranges={ranges}
            type={DATE_RANGE_TYPE}
            handleDateRangeMutation={handleDateRangeMutation}
            numberMonthDisplayByType={{ custom: 2 }}
            hasTimeRange={false}
          />
        </PopoverContent>
      </Popover>
      {error ? (
        <div id={propId ? `${propId}-error` : undefined} className={styles.error} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

export default ConditionalFiltersDateRange;
